import React, { useState, useRef, useEffect } from 'react';
import { PageView } from '../types';
import { Menu, X, ArrowUpRight, ChevronDown, Layers, Activity, Terminal, Linkedin } from 'lucide-react';
import { FOUNDER_CONFIG } from '../config/conference';

interface NavbarProps {
  activePage: PageView;
  setActivePage: (page: PageView) => void;
  onOpenLeadModal: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({ activePage, setActivePage, onOpenLeadModal }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const mainLinks: { id: PageView; label: string }[] = [
    { id: 'home', label: 'Overview' },
    { id: 'features', label: 'Capabilities' },
    { id: 'why-muse', label: 'Philosophy' },
  ];

  const solutionLinks: { id: PageView; label: string; desc: string; icon: React.ReactNode }[] = [
    {
      id: 'consolidation',
      label: 'Consolidation',
      desc: 'Stack consolidation & per-subscriber pricing for ISPs',
      icon: <Layers size={16} className="item-icon" />
    },
    {
      id: 'ops',
      label: 'Outage Response',
      desc: 'Topology event clustering & noise suppression for NOCs',
      icon: <Activity size={16} className="item-icon" />
    },
    {
      id: 'forge',
      label: 'Forge Sandbox',
      desc: 'Interactive custom hardware schema generator',
      icon: <Terminal size={16} className="item-icon" />
    },
  ];

  const isSolutionsActive = activePage === 'consolidation' || activePage === 'ops' || activePage === 'forge';

  const handleNavClick = (id: PageView) => {
    setActivePage(id);
    setDropdownOpen(false);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDropdownOpen(false);
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <header className="nav-header">
      <div className="section-container nav-inner">
        {/* Brand Mark with SVG Favicon Logo */}
        <div className="nav-brand" onClick={() => handleNavClick('home')}>
          <img src="/favicon.svg" alt="MUSE Telemetry Logo" className="brand-logo-img" />
          <span className="brand-title">MUSE TELEMETRY</span>
        </div>

        {/* Desktop Links */}
        <nav className="desktop-links">
          {mainLinks.map((link) => (
            <button
              key={link.id}
              onClick={() => handleNavClick(link.id)}
              className={`nav-btn ${activePage === link.id ? 'active' : ''}`}
            >
              {link.label}
            </button>
          ))}

          {/* Solutions Dropdown */}
          <div
            className="nav-dropdown-wrapper"
            ref={dropdownRef}
            onMouseEnter={() => setDropdownOpen(true)}
            onMouseLeave={() => setDropdownOpen(false)}
          >
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`nav-btn dropdown-toggle ${isSolutionsActive ? 'active' : ''} ${dropdownOpen ? 'open' : ''}`}
              aria-expanded={dropdownOpen}
              aria-haspopup="true"
            >
              <span>Solutions</span>
              <ChevronDown size={14} className={`chevron-icon ${dropdownOpen ? 'rotate' : ''}`} />
            </button>

            {dropdownOpen && (
              <div className="nav-dropdown-menu">
                <div className="dropdown-arrow-indicator" />
                <div className="dropdown-items">
                  {solutionLinks.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleNavClick(item.id)}
                      className={`dropdown-item ${activePage === item.id ? 'item-active' : ''}`}
                    >
                      <div className="dropdown-icon-col">{item.icon}</div>
                      <div className="dropdown-text-col">
                        <div className="dropdown-item-title">{item.label}</div>
                        <div className="dropdown-item-desc">{item.desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Articles */}
          <button
            onClick={() => handleNavClick('blog')}
            className={`nav-btn ${activePage === 'blog' ? 'active' : ''}`}
          >
            Articles
          </button>
        </nav>

        {/* Right CTA Group */}
        <div className="nav-actions">
          <a
            href={FOUNDER_CONFIG.linkedinUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="app-link linkedin-nav-link"
            title={`Connect with ${FOUNDER_CONFIG.name} on LinkedIn`}
          >
            <Linkedin size={14} />
            <span>LinkedIn</span>
          </a>

          <a
            href="https://demo.musetelemetry.com"
            target="_blank"
            rel="noopener noreferrer"
            className="app-link"
          >
            <span>Live Demo</span>
            <ArrowUpRight size={13} />
          </a>

          <button onClick={onOpenLeadModal} className="btn btn-primary btn-sm">
            Book 15-Min Review
          </button>

          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle Menu"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className="mobile-drawer">
          <div className="mobile-link-list">
            {mainLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => handleNavClick(link.id)}
                className={`mobile-link-btn ${activePage === link.id ? 'active' : ''}`}
              >
                {link.label}
              </button>
            ))}

            {/* Solutions Subsection in Mobile */}
            <div className="mobile-section-divider">
              <span className="mobile-section-heading">SOLUTIONS</span>
            </div>

            {solutionLinks.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                className={`mobile-link-btn mobile-sub-link ${activePage === item.id ? 'active' : ''}`}
              >
                <span className="mobile-sub-icon">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}

            <div className="mobile-section-divider" />

            <button
              onClick={() => handleNavClick('blog')}
              className={`mobile-link-btn ${activePage === 'blog' ? 'active' : ''}`}
            >
              Articles
            </button>
          </div>

          <div className="mobile-drawer-actions">
            <button onClick={() => { setMobileMenuOpen(false); onOpenLeadModal(); }} className="btn btn-primary w-full">
              Book 15-Min Review
            </button>
            <a
              href={FOUNDER_CONFIG.linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary w-full"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.45rem' }}
            >
              <Linkedin size={15} />
              <span>Connect with BJ Muse</span>
              <ArrowUpRight size={13} />
            </a>
            <a
              href="https://demo.musetelemetry.com"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary w-full"
            >
              Live Demo
            </a>
          </div>
        </div>
      )}

      <style>{`
        .nav-header {
          position: sticky;
          top: 0;
          z-index: 100;
          background: rgba(0, 0, 0, 0.95);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border-bottom: 1px solid var(--border-color);
          padding: 0.85rem 0;
        }

        .nav-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .nav-brand {
          display: flex;
          align-items: center;
          gap: 0.65rem;
          cursor: pointer;
        }

        .brand-logo-img {
          width: 24px;
          height: 24px;
          border-radius: 6px;
          display: block;
        }

        .brand-title {
          font-family: var(--font-body);
          font-weight: 700;
          font-size: 0.95rem;
          letter-spacing: -0.02em;
          color: #ffffff;
        }

        .desktop-links {
          display: flex;
          align-items: center;
          gap: 2rem;
        }

        .nav-btn {
          background: transparent;
          border: none;
          color: var(--text-muted);
          font-size: 0.85rem;
          font-weight: 400;
          cursor: pointer;
          transition: color 0.15s ease;
          padding: 0.25rem 0;
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
        }

        .nav-btn:hover {
          color: #ffffff;
        }

        .nav-btn.active {
          color: #ffffff;
          font-weight: 600;
        }

        /* Dropdown Wrapper & Button */
        .nav-dropdown-wrapper {
          position: relative;
          padding: 0.5rem 0;
        }

        .dropdown-toggle {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
        }

        .chevron-icon {
          transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1);
          color: var(--text-dim);
        }

        .nav-btn:hover .chevron-icon,
        .dropdown-toggle.open .chevron-icon,
        .dropdown-toggle.active .chevron-icon {
          color: #ffffff;
        }

        .chevron-icon.rotate {
          transform: rotate(180deg);
        }

        /* Dropdown Floating Menu */
        .nav-dropdown-menu {
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          width: 330px;
          background: #080808;
          border: 1px solid var(--border-subtle);
          border-radius: var(--radius-md);
          padding: 0.6rem;
          box-shadow: 0 16px 36px rgba(0, 0, 0, 0.85), 0 0 0 1px rgba(255, 255, 255, 0.05);
          animation: dropdownFadeIn 0.18s cubic-bezier(0.16, 1, 0.3, 1);
          z-index: 120;
        }

        @keyframes dropdownFadeIn {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-6px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }

        .dropdown-items {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .dropdown-item {
          display: flex;
          align-items: flex-start;
          gap: 0.85rem;
          padding: 0.75rem 0.85rem;
          background: transparent;
          border: 1px solid transparent;
          border-radius: var(--radius-sm);
          text-align: left;
          cursor: pointer;
          transition: all 0.15s ease;
          width: 100%;
        }

        .dropdown-item:hover {
          background: #121212;
          border-color: var(--border-color);
        }

        .dropdown-item.item-active {
          background: #141414;
          border-color: var(--border-subtle);
        }

        .dropdown-icon-col {
          padding-top: 0.1rem;
          color: #3b82f6;
          flex-shrink: 0;
        }

        .dropdown-item:hover .dropdown-icon-col {
          color: #60a5fa;
        }

        .dropdown-text-col {
          display: flex;
          flex-direction: column;
          gap: 0.2rem;
        }

        .dropdown-item-title {
          font-size: 0.88rem;
          font-weight: 600;
          color: #ffffff;
          line-height: 1.2;
        }

        .dropdown-item-desc {
          font-size: 0.75rem;
          color: var(--text-muted);
          line-height: 1.35;
        }

        /* Right CTA Actions */
        .nav-actions {
          display: flex;
          align-items: center;
          gap: 1.25rem;
        }

        .app-link {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          color: var(--text-muted);
          font-size: 0.85rem;
          text-decoration: none;
          transition: color 0.15s;
        }

        .app-link:hover {
          color: #ffffff;
        }

        .btn-sm {
          padding: 0.45rem 1rem;
          font-size: 0.82rem;
        }

        /* Mobile Styles */
        .mobile-menu-btn {
          display: none;
          background: none;
          border: none;
          color: #ffffff;
          cursor: pointer;
        }

        .mobile-drawer {
          display: none;
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: #050505;
          border-bottom: 1px solid var(--border-color);
          padding: 1.5rem;
        }

        .mobile-link-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-bottom: 1.25rem;
        }

        .mobile-section-divider {
          padding-top: 0.5rem;
          border-top: 1px solid var(--border-color);
          margin: 0.25rem 0;
        }

        .mobile-section-heading {
          font-size: 0.68rem;
          font-family: var(--font-mono);
          color: var(--text-dim);
          letter-spacing: 0.06em;
        }

        .mobile-link-btn {
          background: none;
          border: none;
          color: var(--text-muted);
          font-size: 1rem;
          text-align: left;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .mobile-sub-link {
          font-size: 0.92rem;
          padding-left: 0.5rem;
        }

        .mobile-sub-icon {
          color: #3b82f6;
          display: inline-flex;
          align-items: center;
        }

        .mobile-link-btn.active {
          color: #ffffff;
          font-weight: 600;
        }

        .mobile-drawer-actions {
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
        }

        .w-full { width: 100%; }

        @media (max-width: 850px) {
          .desktop-links { display: none; }
          .app-link { display: none; }
          .mobile-menu-btn { display: block; }
          .mobile-drawer { display: block; }
        }
      `}</style>
    </header>
  );
};

export default Navbar;
